/**
 * Client view of the listener no-show deadline.
 *
 * Grace is booking start + 4:00. Talk now start is accept/createdAt, not
 * the later Flex 15-minute seat. A stored Flex start + 4 min is why the
 * live room showed ~9:45 left after the real start + 4:00 had passed.
 * See server/api-util/sessionNoShow.js.
 */

import {
  LISTENER_NO_SHOW_GRACE_MS,
  bookingTimes,
  txPastListenerNoShowDeadline,
  txListenerHasJoined,
} from './sessionClock';
import { isSupersededEditedBooking } from './editUpcomingBooking';

export { LISTENER_NO_SHOW_GRACE_MS, txPastListenerNoShowDeadline, txListenerHasJoined };

const toEpochMs = value => {
  if (value == null || value === '') {
    return NaN;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : NaN;
  }
  const ms = Number(value);
  return Number.isFinite(ms) ? ms : NaN;
};

/**
 * Cancel clock: the earlier of server eligibleAt and tx start + 4:00.
 * Using the later Flex seat (or session end) was the ~10 minute bug.
 *
 * @param {Object} [params]
 * @param {number|string|null} [params.noShowEligibleAt]
 * @param {number|string|null} [params.startMs]
 * @returns {number|null}
 */
export const noShowDeadlineMs = ({ noShowEligibleAt, startMs } = {}) => {
  const fromStart = toEpochMs(startMs);
  const fromStartDeadline = Number.isFinite(fromStart)
    ? fromStart + LISTENER_NO_SHOW_GRACE_MS
    : NaN;
  const fromServer = toEpochMs(noShowEligibleAt);
  const candidates = [fromServer, fromStartDeadline].filter(Number.isFinite);
  if (!candidates.length) {
    return null;
  }
  return Math.min(...candidates);
};

/**
 * Milliseconds left until the no-show cancel time.
 *
 * @param {number|string|null} [eligibleAt]
 * @param {number} [nowMs]
 * @returns {number|null}
 */
export const noShowRemainingMs = (eligibleAt, nowMs = Date.now()) => {
  const at = toEpochMs(eligibleAt);
  if (!Number.isFinite(at)) {
    return null;
  }
  return at - nowMs;
};

/**
 * True when the listener never joined and the server deadline has passed.
 * Does not require talkerWaiting — that flag is in-memory and can be lost.
 *
 * @param {Object} [params]
 * @param {boolean} [params.ended]
 * @param {boolean} [params.talkerWaiting]
 * @param {boolean} [params.listenerJoined]
 * @param {number|string|null} [params.noShowEligibleAt]
 * @param {number} [params.nowMs]
 * @returns {boolean}
 */
export const isNoShowDeadlineReached = ({
  ended = false,
  listenerJoined = false,
  noShowEligibleAt,
  startMs,
  nowMs = Date.now(),
} = {}) => {
  if (ended || listenerJoined) {
    return false;
  }
  const deadline = noShowDeadlineMs({ noShowEligibleAt, startMs });
  const remaining = noShowRemainingMs(deadline, nowMs);
  return remaining != null && remaining <= 0;
};

/**
 * True when the listener joined, the talker never joined, and start + 4
 * has passed.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const isTalkerNoShowDeadlineReached = ({
  ended = false,
  talkerJoined = false,
  listenerJoined = false,
  noShowEligibleAt,
  startMs,
  nowMs = Date.now(),
} = {}) => {
  if (ended || talkerJoined || !listenerJoined) {
    return false;
  }
  const deadline = noShowDeadlineMs({ noShowEligibleAt, startMs });
  const remaining = noShowRemainingMs(deadline, nowMs);
  return remaining != null && remaining <= 0;
};

const NO_SHOW_CANCEL_TRANSITIONS = new Set([
  'transition/customer-cancel',
  'transition/provider-cancel',
  'transition/cancel',
  'transition/expire-accepted',
]);

/**
 * True when this booking is a listener no-show cancel. Uses Flex flags
 * when present; if a seat omits them, a cancel with no listener join
 * after start + 4:00 still counts (fresh load after the sweep).
 *
 * @param {Object} [tx]
 * @param {Date} [now]
 * @returns {boolean}
 */
export const txIsListenerNoShow = (tx, now = new Date()) => {
  const data = tx?.attributes?.protectedData || {};
  const meta = tx?.attributes?.metadata || {};
  if (
    data.listenerNoShow === true ||
    data.endedReason === 'listener_no_show' ||
    meta.listenerNoShow === true ||
    meta.endedReason === 'listener_no_show'
  ) {
    return true;
  }
  if (data.talkerNoShow === true || data.endedReason === 'talker_no_show') {
    return false;
  }
  if (!NO_SHOW_CANCEL_TRANSITIONS.has(tx?.attributes?.lastTransition) || txListenerHasJoined(tx)) {
    return false;
  }
  // A booking replaced by a reschedule is not a no-show.
  if (isSupersededEditedBooking(tx)) {
    return false;
  }
  const start = bookingTimes(tx).start;
  // The talker cancelled (or rescheduled) before the session started — the
  // listener cannot have missed it.
  const lastRaw = tx?.attributes?.lastTransitionedAt;
  const lastAt =
    lastRaw instanceof Date
      ? lastRaw.getTime()
      : typeof lastRaw === 'number'
      ? lastRaw
      : Date.parse(lastRaw || '');
  if (
    tx?.attributes?.lastTransition === 'transition/customer-cancel' &&
    start &&
    Number.isFinite(lastAt) &&
    lastAt < start.getTime()
  ) {
    return false;
  }
  return !!(start && now.getTime() >= start.getTime() + LISTENER_NO_SHOW_GRACE_MS);
};
